import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  DollarSign
} from 'lucide-react';
import { escrowService, EscrowAccount, EscrowTransaction } from '@/lib/escrow';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export function EscrowDashboard() {
  const [escrowAccount, setEscrowAccount] = useState<EscrowAccount | null>(null);
  const [transactions, setTransactions] = useState<EscrowTransaction[]>([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState<string>('unverified');
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadEscrowData();

    // Re-fetch when tab/window regains focus (e.g., after KYC approval)
    const handleFocus = () => loadEscrowData();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  const loadEscrowData = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Fetch KYC status
      const { data: profile } = await supabase
        .from('profiles')
        .select('kyc_status, verified')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profile) {
        setKycStatus(profile.kyc_status || 'unverified');
        setIsVerified(profile.verified === true);
        setKycStatus(profile.kyc_status || 'unverified');
      }
      // Fetch escrow account
      const { data: accountData, error: accountError } = await supabase
        .from('escrow_accounts')
        .select('*')
        .eq('seller_id', user.id)
        .maybeSingle();

      if (accountError && accountError.code !== 'PGRST116') {
        console.error('Error fetching escrow account:', accountError);
      }

      // Fetch escrow transactions
      const { data: txnData, error: txnError } = await supabase
        .from('escrow_transactions')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (txnError) {
        console.error('Error fetching transactions:', txnError);
      }

      // Map the data to the expected format
      if (accountData) {
        setEscrowAccount({
          id: accountData.id,
          seller_id: accountData.seller_id,
          available_balance: Number(accountData.balance_available || 0),
          pending_balance: Number(accountData.balance_pending || 0),
          total_released: Number(accountData.balance_released || 0),
          created_at: accountData.created_at,
          updated_at: accountData.updated_at
        });
      }

      if (txnData) {
        setTransactions(txnData.map((tx: any) => ({
          id: tx.id,
          order_id: tx.order_id,
          seller_id: tx.seller_id,
          buyer_id: tx.buyer_id,
          amount: Number(tx.amount),
          status: tx.status === 'completed' && tx.type === 'release' ? 'released' : 
                  tx.status === 'completed' && tx.type === 'hold' ? 'held' : tx.status,
          type: tx.type,
          transaction_type: tx.type === 'release' ? 'payment_released' : 
                           tx.type === 'hold' ? 'payment_held' : tx.type,
          description: tx.description,
          released_at: tx.type === 'release' ? tx.created_at : undefined,
          created_at: tx.created_at
        })));
      }
    } catch (error) {
      console.error('Error loading escrow data:', error);
      toast({
        title: "Error loading escrow data",
        description: "Unable to fetch your escrow information",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawal = async () => {
    if (!user) return;

    if (!isVerified || kycStatus !== 'approved') {
      toast({
        title: "KYC verification required",
        description: "KYC verification required before withdrawal.",
        variant: "destructive"
      });
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid withdrawal amount",
        variant: "destructive"
      });
      return;
    }

    if (amount > availableBalance) {
      toast({
        title: "Insufficient balance",
        description: `You can only withdraw up to ₦${availableBalance.toLocaleString()}`,
        variant: "destructive"
      });
      return;
    }

    try {
      // Fetch user's bank accounts using secure RPC function
      const { data: bankAccounts, error: bankError } = await supabase
        .rpc('get_user_bank_accounts');

      if (bankError) throw bankError;

      if (!bankAccounts || bankAccounts.length === 0) {
        toast({
          title: "No bank account",
          description: "Please add a verified bank account before requesting withdrawal",
          variant: "destructive"
        });
        return;
      }

      // Use default bank account or first verified one
      const defaultAccount = bankAccounts.find(acc => acc.is_default && acc.status === 'verified') || bankAccounts.find(acc => acc.status === 'verified');

      if (!defaultAccount) {
        toast({
          title: "No verified bank account",
          description: "Please add and verify a bank account before requesting withdrawal",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase.rpc('request_escrow_withdrawal', {
        p_seller_id: user.id,
        p_amount: amount,
        p_bank_account_id: defaultAccount.id
      });

      if (error) throw error;

      toast({
        title: "Withdrawal requested",
        description: `₦${amount.toLocaleString()} withdrawal to ${defaultAccount.bank_name} (${defaultAccount.account_number_masked}) has been submitted for processing`,
      });
      setWithdrawAmount('');
      await loadEscrowData(); // Reload data to show updated balances
    } catch (error: any) {
      console.error('Withdrawal error:', error);
      toast({
        title: "Withdrawal failed",
        description: error.message || "Failed to process withdrawal request",
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'held': return <Clock className="h-4 w-4" />;
      case 'released': return <CheckCircle className="h-4 w-4" />;
      case 'refunded': return <AlertCircle className="h-4 w-4" />;
      default: return <Shield className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'held': return 'bg-amber-100 text-amber-700';
      case 'released': return 'bg-emerald-100 text-emerald-700';
      case 'refunded': return 'bg-rose-100 text-rose-700';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const availableBalance = escrowAccount?.available_balance || 0;
  const pendingBalance = escrowAccount?.pending_balance || 0;
  const totalBalance = availableBalance + pendingBalance;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          Escrow Protection
        </h2>
        <p className="text-muted-foreground mt-1">
          All payments are secured until buyers confirm delivery
        </p>
      </div>

      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Balance</CardTitle>
            <Wallet className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦{availableBalance.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Ready for withdrawal</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Balance</CardTitle>
            <Lock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦{pendingBalance.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting buyer confirmation</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Released</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦{(escrowAccount?.total_released || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Lifetime earnings</p>
          </CardContent>
        </Card>
      </div>

      {/* Balance Breakdown */}
      {totalBalance > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Balance Distribution</CardTitle>
            <CardDescription>Your funds status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Available ({((availableBalance / totalBalance) * 100).toFixed(0)}%)</span>
                  <span className="font-medium">₦{availableBalance.toLocaleString()}</span>
                </div>
                <Progress value={(availableBalance / totalBalance) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Pending ({((pendingBalance / totalBalance) * 100).toFixed(0)}%)</span>
                  <span className="font-medium">₦{pendingBalance.toLocaleString()}</span>
                </div>
                <Progress value={(pendingBalance / totalBalance) * 100} className="h-2 [&>div]:bg-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Withdrawal Section */}
      <Card>
        <CardHeader>
          <CardTitle>Request Withdrawal</CardTitle>
          <CardDescription>Transfer available funds to your bank account</CardDescription>
        </CardHeader>
        <CardContent>
          {(!isVerified || kycStatus !== 'approved') ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium">KYC verification required before withdrawal.</p>
            </div>
          ) : (
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Enter amount to withdraw"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  max={availableBalance}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Maximum: ₦{availableBalance.toLocaleString()}
                </p>
              </div>
              <Button 
                onClick={handleWithdrawal}
                disabled={!withdrawAmount || parseFloat(withdrawAmount) > availableBalance}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Withdraw
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Escrow Transactions</CardTitle>
          <CardDescription>Recent escrow activity</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList className="w-full overflow-x-auto flex justify-start">
              <TabsTrigger value="all" className="whitespace-nowrap flex-shrink-0">All</TabsTrigger>
              <TabsTrigger value="held" className="whitespace-nowrap flex-shrink-0">Pending</TabsTrigger>
              <TabsTrigger value="released" className="whitespace-nowrap flex-shrink-0">Released</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-4 mt-4">
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No transactions yet
                </div>
              ) : (
                transactions.map((txn) => (
                  <div key={txn.id} className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${getStatusColor(txn.status)}`}>
                        {getStatusIcon(txn.status)}
                      </div>
                      <div>
                        <p className="font-medium">{txn.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">₦{txn.amount.toLocaleString()}</p>
                      <Badge className={getStatusColor(txn.status)}>
                        {txn.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
            
            <TabsContent value="held" className="space-y-4 mt-4">
              {transactions.filter(t => t.status === 'held').map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-full bg-amber-100 text-amber-700">
                      <Clock className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium">{txn.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">₦{txn.amount.toLocaleString()}</p>
                    <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                  </div>
                </div>
              ))}
            </TabsContent>
            
            <TabsContent value="released" className="space-y-4 mt-4">
              {transactions.filter(t => t.status === 'released').map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium">{txn.description}</p>
                      <p className="text-sm text-muted-foreground">
                        Released: {txn.released_at ? new Date(txn.released_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">₦{txn.amount.toLocaleString()}</p>
                    <Badge className="bg-emerald-100 text-emerald-700">Released</Badge>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}